/**
 * HealthMetricsSettingsModal Component
 *
 * Modal for editing health metrics configuration from the settings dashboard.
 * This is the SETTINGS modal (saves directly to global settings), NOT the workflow modal
 * (which handles session drafts and "apply to future" logic).
 *
 * Key behaviors:
 * - Manual Save/Discard pattern
 * - Dirty-close blocking: when dirty, backdrop/X/Esc are blocked
 * - Source selector with confirmation when changing (if metrics exist)
 * - SortableList for drag-and-drop reordering
 * - Desktop: inline editing; Mobile: tap-to-edit modal
 * - All validation errors are blocking (duplicate names block save)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Input,
  NumberInput,
  Card,
  CardBody,
  SortableList,
  DragHandle,
  EmptyState,
  useToastNotification,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { generateUUID } from '@/utils';
import { createDefaultMetricsForSource } from '@/config/defaults';

// ============================================================================
// Types
// ============================================================================

/**
 * Health metric with required id for SortableList
 */
interface HealthMetric {
  id: string;
  name: string;
  min: number;
  max: number;
  unit?: string;
}

/**
 * Source options for health data
 */
type HealthMetricSource = 'apple' | 'whoop' | 'oura' | 'garmin' | 'custom';

const SOURCE_OPTIONS: Array<{ key: HealthMetricSource; label: string }> = [
  { key: 'apple', label: 'Apple Watch' },
  { key: 'whoop', label: 'Whoop' },
  { key: 'oura', label: 'Oura' },
  { key: 'garmin', label: 'Garmin' },
  { key: 'custom', label: 'Custom' },
];

/**
 * Validation error state for a single metric (all errors are blocking)
 */
interface MetricValidationErrors {
  name?: string;
  min?: string;
  max?: string;
}

export interface HealthMetricsSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Async callback when settings are saved - should persist to global settings */
  onSave: (source: HealthMetricSource, metrics: HealthMetric[]) => Promise<void>;
  /** Initial source from current settings */
  initialSource: HealthMetricSource;
  /** Initial metrics from current settings */
  initialMetrics: HealthMetric[];
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate a single metric
 * All errors are blocking (prevent save)
 */
function validateMetric(
  metric: HealthMetric,
  allMetrics: HealthMetric[]
): MetricValidationErrors {
  const errors: MetricValidationErrors = {};
  const trimmedName = metric.name.trim();

  // Name required
  if (!trimmedName) {
    errors.name = 'Name is required';
  } else {
    // Name must be unique (case-insensitive) - blocking error
    const isDuplicate = allMetrics.some(
      (m) =>
        m.id !== metric.id &&
        m.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      errors.name = 'Name must be unique';
    }
  }

  // Min required
  if (isNaN(metric.min)) {
    errors.min = 'Min is required';
  }

  // Max required and must be > min
  if (isNaN(metric.max)) {
    errors.max = 'Max is required';
  } else if (!isNaN(metric.min) && metric.max <= metric.min) {
    errors.max = 'Max must be greater than min';
  }

  return errors;
}

/**
 * Check if any metric has blocking errors
 */
function hasBlockingErrors(
  validationErrors: Record<string, MetricValidationErrors>
): boolean {
  return Object.values(validationErrors).some(
    (errors) => Object.keys(errors).length > 0
  );
}

// ============================================================================
// Component
// ============================================================================

export const HealthMetricsSettingsModal: React.FC<HealthMetricsSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSource,
  initialMetrics,
}) => {
  const { showToast } = useToastNotification();

  // Local editing state
  const [localSource, setLocalSource] = useState<HealthMetricSource>('custom');
  const [localMetrics, setLocalMetrics] = useState<HealthMetric[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, MetricValidationErrors>
  >({});

  // Baseline refs for dirty tracking
  const baselineSourceRef = useRef<HealthMetricSource>('custom');
  const baselineMetricsKeyRef = useRef<string>('');

  // Source change confirmation state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingSource, setPendingSource] = useState<HealthMetricSource | null>(null);

  // Mobile edit modal state
  const [editingMetricId, setEditingMetricId] = useState<string | 'new' | null>(null);
  const [editingDraft, setEditingDraft] = useState<HealthMetric | null>(null);
  const [editModalErrors, setEditModalErrors] = useState<MetricValidationErrors>({});

  // Track previous isOpen state to detect open transition (false → true)
  const wasOpenRef = useRef(false);

  // Compute dirty state using stable comparison
  const localMetricsKey = useMemo(() => JSON.stringify(localMetrics), [localMetrics]);
  const isDirty =
    localSource !== baselineSourceRef.current ||
    localMetricsKey !== baselineMetricsKeyRef.current;

  // Check if save should be disabled (has blocking errors)
  const hasSaveBlockingErrors = hasBlockingErrors(validationErrors);

  // Initialize local state from props when modal opens
  // Changes to initialSource/initialMetrics while modal is open are ignored
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Deep copy metrics
      const metricsCopy: HealthMetric[] = initialMetrics.map((m) => {
        const metric: HealthMetric = {
          id: m.id,
          name: m.name,
          min: m.min,
          max: m.max,
        };
        if (m.unit !== undefined) {
          metric.unit = m.unit;
        }
        return metric;
      });

      // Set local state and baselines
      setLocalSource(initialSource);
      setLocalMetrics(metricsCopy);
      baselineSourceRef.current = initialSource;
      baselineMetricsKeyRef.current = JSON.stringify(metricsCopy);

      // Reset ALL state to prevent stale UI
      setValidationErrors({});
      setShowConfirmModal(false);
      setPendingSource(null);
      setEditingMetricId(null);
      setEditingDraft(null);
      setEditModalErrors({});
      setIsSaving(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, initialSource, initialMetrics]);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateAllMetrics = useCallback((metricsToValidate: HealthMetric[]) => {
    const errors: Record<string, MetricValidationErrors> = {};
    metricsToValidate.forEach((metric) => {
      const metricErrors = validateMetric(metric, metricsToValidate);
      if (Object.keys(metricErrors).length > 0) {
        errors[metric.id] = metricErrors;
      }
    });
    setValidationErrors(errors);
    return errors;
  }, []);

  // ============================================================================
  // Source Change Handlers
  // ============================================================================

  const handleSourceSelection = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') return;
      const selectedKey = Array.from(keys)[0] as HealthMetricSource | undefined;
      if (!selectedKey || selectedKey === localSource) return;

      // If no metrics exist, apply immediately
      if (localMetrics.length === 0) {
        const newMetrics = createDefaultMetricsForSource(selectedKey) as HealthMetric[];
        setLocalSource(selectedKey);
        setLocalMetrics(newMetrics);
        setValidationErrors({});
        return;
      }

      // Show confirmation dialog
      setPendingSource(selectedKey);
      setShowConfirmModal(true);
    },
    [localSource, localMetrics.length]
  );

  const handleConfirmSourceChange = useCallback(() => {
    if (pendingSource) {
      const newMetrics = createDefaultMetricsForSource(pendingSource) as HealthMetric[];
      setLocalSource(pendingSource);
      setLocalMetrics(newMetrics);
      setValidationErrors({});
    }
    setShowConfirmModal(false);
    setPendingSource(null);
  }, [pendingSource]);

  const handleCancelSourceChange = useCallback(() => {
    setShowConfirmModal(false);
    setPendingSource(null);
  }, []);

  // ============================================================================
  // Metric CRUD Handlers (Desktop Inline)
  // ============================================================================

  const handleMetricChange = useCallback(
    (id: string, field: keyof HealthMetric, value: string | number) => {
      setLocalMetrics((prev) => {
        const updated = prev.map((m) =>
          m.id === id ? { ...m, [field]: value } : m
        );
        validateAllMetrics(updated);
        return updated;
      });
    },
    [validateAllMetrics]
  );

  const handleAddMetric = useCallback(() => {
    const newMetric: HealthMetric = {
      id: generateUUID(),
      name: '',
      min: 0,
      max: 100,
      unit: '',
    };
    setLocalMetrics((prev) => {
      const updated = [...prev, newMetric];
      validateAllMetrics(updated);
      return updated;
    });
  }, [validateAllMetrics]);

  const handleDeleteMetric = useCallback(
    (id: string) => {
      setLocalMetrics((prev) => {
        const updated = prev.filter((m) => m.id !== id);
        setValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[id];
          return newErrors;
        });
        validateAllMetrics(updated);
        return updated;
      });
    },
    [validateAllMetrics]
  );

  const handleReorder = useCallback(
    (reorderedMetrics: HealthMetric[]) => {
      setLocalMetrics(reorderedMetrics);
      validateAllMetrics(reorderedMetrics);
    },
    [validateAllMetrics]
  );

  // ============================================================================
  // Mobile Edit Modal Handlers
  // ============================================================================

  const handleOpenEditModal = useCallback((metric: HealthMetric) => {
    setEditingMetricId(metric.id);
    setEditingDraft({ ...metric });
    setEditModalErrors({});
  }, []);

  const handleOpenAddModal = useCallback(() => {
    const newMetric: HealthMetric = {
      id: generateUUID(),
      name: '',
      min: 0,
      max: 100,
      unit: '',
    };
    setEditingMetricId('new');
    setEditingDraft(newMetric);
    setEditModalErrors({});
  }, []);

  const handleEditDraftChange = useCallback(
    (field: keyof HealthMetric, value: string | number) => {
      setEditingDraft((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, [field]: value };
        // Validate draft against all metrics
        const otherMetrics =
          editingMetricId === 'new'
            ? localMetrics
            : localMetrics.filter((m) => m.id !== editingMetricId);
        const errors = validateMetric(updated, [...otherMetrics, updated]);
        setEditModalErrors(errors);
        return updated;
      });
    },
    [editingMetricId, localMetrics]
  );

  const handleSaveEditModal = useCallback(() => {
    if (!editingDraft) return;

    // Validate before saving
    const otherMetrics =
      editingMetricId === 'new'
        ? localMetrics
        : localMetrics.filter((m) => m.id !== editingMetricId);
    const errors = validateMetric(editingDraft, [...otherMetrics, editingDraft]);

    if (Object.keys(errors).length > 0) {
      setEditModalErrors(errors);
      return;
    }

    // Normalize the metric: empty unit → undefined
    const normalizedMetric: HealthMetric = {
      id: editingDraft.id,
      name: editingDraft.name.trim(),
      min: editingDraft.min,
      max: editingDraft.max,
    };
    if (editingDraft.unit?.trim()) {
      normalizedMetric.unit = editingDraft.unit.trim();
    }

    // Update local metrics
    let updated: HealthMetric[];
    if (editingMetricId === 'new') {
      updated = [...localMetrics, normalizedMetric];
    } else {
      updated = localMetrics.map((m) =>
        m.id === editingMetricId ? normalizedMetric : m
      );
    }

    setLocalMetrics(updated);
    validateAllMetrics(updated);

    // Close modal
    setEditingMetricId(null);
    setEditingDraft(null);
    setEditModalErrors({});
  }, [editingDraft, editingMetricId, localMetrics, validateAllMetrics]);

  const handleCancelEditModal = useCallback(() => {
    setEditingMetricId(null);
    setEditingDraft(null);
    setEditModalErrors({});
  }, []);

  // ============================================================================
  // Save/Discard Handlers
  // ============================================================================

  const handleSave = useCallback(async () => {
    // Validate all metrics
    const currentErrors = validateAllMetrics(localMetrics);
    if (hasBlockingErrors(currentErrors)) {
      showToast({
        title: 'Validation errors',
        description: 'Please fix the errors before saving.',
        color: 'warning',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Normalize metrics: empty unit → undefined
      const normalizedMetrics: HealthMetric[] = localMetrics.map((m) => {
        const metric: HealthMetric = {
          id: m.id,
          name: m.name.trim(),
          min: m.min,
          max: m.max,
        };
        if (m.unit?.trim()) {
          metric.unit = m.unit.trim();
        }
        return metric;
      });

      await onSave(localSource, normalizedMetrics);

      // On successful save: update baselines (clears dirty), close modal
      baselineSourceRef.current = localSource;
      baselineMetricsKeyRef.current = JSON.stringify(normalizedMetrics);
      setLocalMetrics(normalizedMetrics); // Sync local state with normalized
      onClose();
    } catch (err) {
      // On save failure: keep modal open, show error toast
      console.error('Save error:', err);
      showToast({
        title: 'Save failed',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [localSource, localMetrics, validateAllMetrics, onSave, onClose, showToast]);

  const handleDiscard = useCallback(() => {
    // Reset to baselines, clear validation errors
    setLocalSource(baselineSourceRef.current);
    const baselineMetrics = JSON.parse(baselineMetricsKeyRef.current) as HealthMetric[];
    setLocalMetrics(baselineMetrics);
    setValidationErrors({});
    onClose();
  }, [onClose]);

  // ============================================================================
  // Metric Row Renderer
  // ============================================================================

  const renderMetricRow = useCallback(
    (metric: HealthMetric, dragHandleProps: DragHandleProps) => {
      const errors = validationErrors[metric.id] || {};
      const hasError = Object.keys(errors).length > 0;
      const rangeDisplay = `${metric.min}–${metric.max}${metric.unit ? ` ${metric.unit}` : ''}`;

      return (
        <Card
          shadow="sm"
          radius="sm"
          className={dragHandleProps.isDragging ? 'opacity-50 shadow-lg' : ''}
        >
          <CardBody className="p-3">
            {/* Mobile Layout - Compact, tap to edit */}
            <div className="md:hidden flex items-center gap-3">
            <DragHandle dragHandleProps={dragHandleProps} />

            {/* Tappable content area */}
            <button
              type="button"
              className="flex-1 text-left"
              onClick={() => handleOpenEditModal(metric)}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium ${hasError ? 'text-danger' : ''}`}>
                  {metric.name || 'Unnamed metric'}
                </span>
                {hasError && <span className="text-danger text-xs">*</span>}
              </div>
              <div className="text-sm text-default-500">{rangeDisplay}</div>
            </button>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => handleOpenEditModal(metric)}
              aria-label={`Edit ${metric.name || 'metric'}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>

            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteMetric(metric.id)}
              aria-label={`Delete ${metric.name || 'metric'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Desktop Layout - Inline editing */}
          <div className="hidden md:flex items-stretch gap-3">
            <DragHandle dragHandleProps={dragHandleProps} />

            {/* Metric Name */}
            <div className="flex-1">
              <Input
                size="sm"
                placeholder="e.g., HRV, Sleep Score"
                value={metric.name}
                onChange={(e) => handleMetricChange(metric.id, 'name', e.target.value)}
                isInvalid={!!errors.name}
                errorMessage={errors.name}
                aria-label="Metric name"
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>

            {/* Min Value */}
            <div className="w-24">
              <NumberInput
                size="sm"
                placeholder="0"
                value={metric.min}
                onValueChange={(val) => handleMetricChange(metric.id, 'min', val ?? 0)}
                isInvalid={!!errors.min}
                errorMessage={errors.min}
                aria-label="Minimum value"
                hideStepper
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>

            {/* Max Value */}
            <div className="w-24">
              <NumberInput
                size="sm"
                placeholder="100"
                value={metric.max}
                onValueChange={(val) => handleMetricChange(metric.id, 'max', val ?? 0)}
                isInvalid={!!errors.max}
                errorMessage={errors.max}
                aria-label="Maximum value"
                hideStepper
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>

            {/* Unit (optional) */}
            <div className="w-20">
              <Input
                size="sm"
                placeholder="e.g., ms"
                value={metric.unit || ''}
                onChange={(e) => handleMetricChange(metric.id, 'unit', e.target.value)}
                aria-label="Unit (optional)"
                classNames={{ inputWrapper: 'h-10' }}
              />
            </div>

            {/* Delete Button */}
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => handleDeleteMetric(metric.id)}
              aria-label={`Delete ${metric.name || 'metric'}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          </CardBody>
        </Card>
      );
    },
    [validationErrors, handleMetricChange, handleDeleteMetric, handleOpenEditModal]
  );

  // Get label for pending source (for confirmation dialog)
  const pendingSourceLabel = pendingSource
    ? SOURCE_OPTIONS.find((o) => o.key === pendingSource)?.label ?? pendingSource
    : '';

  return (
    <>
      {/* Main Config Modal */}
      <Modal
        isOpen={isOpen && !showConfirmModal && editingMetricId === null}
        onClose={onClose}
        placement="top"
        scrollBehavior="inside"
        size="2xl"
        isDismissable={!isDirty}
        hideCloseButton={isDirty}
        isKeyboardDismissDisabled={isDirty}
      >
        <ModalContent>
          <ModalHeader>
            <span className="text-lg font-semibold">Health Metrics</span>
          </ModalHeader>
          <ModalBody className="gap-4">
            {/* Source Selector */}
            <div className="max-w-xs">
              <Select
                label="Fitness Tracker"
                placeholder="Select source"
                selectedKeys={new Set([localSource])}
                onSelectionChange={handleSourceSelection}
                size="sm"
              >
                {SOURCE_OPTIONS.map((option) => (
                  <SelectItem key={option.key}>{option.label}</SelectItem>
                ))}
              </Select>
            </div>

            {/* Metrics List */}
            {localMetrics.length > 0 ? (
              <div className="space-y-2">
                {/* Column Headers (desktop only) */}
                <div className="hidden md:flex items-center gap-3 px-3 text-sm text-default-500 font-medium">
                  <div className="w-6" />
                  <div className="flex-1 pl-6">Metric</div>
                  <div className="w-24">Min</div>
                  <div className="w-24">Max</div>
                  <div className="w-20">Unit</div>
                  <div className="w-8" />
                </div>
                <SortableList
                  items={localMetrics}
                  onReorder={handleReorder}
                  renderItem={renderMetricRow}
                  className="space-y-2"
                />
              </div>
            ) : (
              <EmptyState
                title="No health metrics configured"
                description="Add metrics to track your daily wellness before trading."
              />
            )}

            {/* Add Metric Button - opens modal on mobile, inline on desktop */}
            <div className="md:hidden">
              <Button
                variant="flat"
                color="primary"
                startContent={<Plus className="w-4 h-4" />}
                onPress={handleOpenAddModal}
                className="w-full"
              >
                Add Metric
              </Button>
            </div>
            <div className="hidden md:block">
              <Button
                variant="flat"
                color="primary"
                startContent={<Plus className="w-4 h-4" />}
                onPress={handleAddMetric}
              >
                Add Metric
              </Button>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={isDirty ? handleDiscard : onClose}
              isDisabled={isSaving}
            >
              {isDirty ? 'Discard' : 'Cancel'}
            </Button>
            <Button
              color="primary"
              onPress={handleSave}
              isDisabled={!isDirty || isSaving || hasSaveBlockingErrors}
              isLoading={isSaving}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Source Change Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={handleCancelSourceChange}>
        <ModalContent>
          <ModalHeader>Change Data Source?</ModalHeader>
          <ModalBody>
            <p>
              Switching to <strong>{pendingSourceLabel}</strong> will replace your
              current metrics with the default metrics for that source.
            </p>
            <p className="text-default-500 text-sm mt-2">
              This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelSourceChange}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleConfirmSourceChange}>
              Replace Metrics
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mobile Edit/Add Metric Modal */}
      <Modal
        isOpen={editingMetricId !== null}
        onClose={handleCancelEditModal}
        placement="top"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {editingMetricId === 'new' ? 'Add Metric' : 'Edit Metric'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {editingDraft && (
              <>
                <Input
                  label="Metric Name"
                  placeholder="e.g., HRV, Sleep Score"
                  value={editingDraft.name}
                  onChange={(e) => handleEditDraftChange('name', e.target.value)}
                  isInvalid={!!editModalErrors.name}
                  errorMessage={editModalErrors.name}
                  autoFocus
                  classNames={{ input: 'text-base' }}
                />
                <div className="flex gap-4">
                  <NumberInput
                    label="Min"
                    placeholder="0"
                    value={editingDraft.min}
                    onValueChange={(val) => handleEditDraftChange('min', val ?? 0)}
                    isInvalid={!!editModalErrors.min}
                    errorMessage={editModalErrors.min}
                    hideStepper
                    className="flex-1"
                    classNames={{ input: 'text-base' }}
                  />
                  <NumberInput
                    label="Max"
                    placeholder="100"
                    value={editingDraft.max}
                    onValueChange={(val) => handleEditDraftChange('max', val ?? 0)}
                    isInvalid={!!editModalErrors.max}
                    errorMessage={editModalErrors.max}
                    hideStepper
                    className="flex-1"
                    classNames={{ input: 'text-base' }}
                  />
                </div>
                <Input
                  label="Unit (optional)"
                  placeholder="e.g., ms, %, bpm"
                  value={editingDraft.unit || ''}
                  onChange={(e) => handleEditDraftChange('unit', e.target.value)}
                  classNames={{ input: 'text-base' }}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleCancelEditModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSaveEditModal}
              isDisabled={Object.keys(editModalErrors).length > 0}
            >
              {editingMetricId === 'new' ? 'Add' : 'Save'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
